// Libraries
import React from 'react';

// Supermove
import {Icon, IconSource, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useResponsive} from '@supermove/hooks';
import {ProjectModel, Project} from '@supermove/models';
import {colors} from '@supermove/styles';
import {Datetime} from '@supermove/utils';

// App
import Callout from '@shared/design/components/Callout';
import IconButton from '@shared/design/components/IconButton';
import SkeletonLoader from '@shared/design/components/SkeletonLoader';
import Text from '@shared/design/components/Text';
import ProjectSalesStageBadgeDropdown from '@shared/modules/Project/components/ProjectSalesStageBadgeDropdown';
import ProjectStatusBadge from '@shared/modules/Project/components/ProjectStatusBadge';
import ProjectDetailsBlockWrapper from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockWrapper';
import ProjectTagsList from '@shared/modules/Project/components/widgets/components/ProjectTagsList';
import ProjectTypeLabel from '@shared/modules/Project/components/widgets/components/ProjectTypeLabel';

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
`;

const WidgetOverviewSection = Styled.View`
`;

const SkeletonComponent = () => {
  const responsive = useResponsive();

  return (
    <React.Fragment>
      <SkeletonLoader
        height={
          responsive.desktop
            ? SkeletonLoader.HEIGHT.Typography.Subheading
            : SkeletonLoader.HEIGHT.Typography.Heading2
        }
        width={200}
      />
      <Space height={16} />
      <SkeletonLoader
        height={
          responsive.desktop
            ? SkeletonLoader.HEIGHT.Typography.Body
            : SkeletonLoader.HEIGHT.Typography.Subheading
        }
        width={160}
      />
      <Space height={8} />
      <SkeletonLoader
        height={
          responsive.desktop
            ? SkeletonLoader.HEIGHT.Typography.Micro
            : SkeletonLoader.HEIGHT.Typography.Body
        }
        width={200}
      />
      <Space height={24} />
      <SkeletonLoader
        height={
          responsive.desktop
            ? SkeletonLoader.HEIGHT.Typography.Body
            : SkeletonLoader.HEIGHT.Typography.Subheading
        }
        width={200}
      />
      <Space height={20} />
      <ProjectDetailLoading icon={Icon.Calendar} />
      <ProjectDetailLoading icon={Icon.WeightHanging} />
      <ProjectDetailLoading icon={Icon.RulerTriangle} />
      <ProjectDetailLoading icon={Icon.MapPin} />
    </React.Fragment>
  );
};

const ProjectDetailLoading = ({icon}: {icon: IconSource}) => {
  const responsive = useResponsive();

  return (
    <React.Fragment>
      <Space height={8} />
      <Row>
        <IconButton source={icon} isSecondary isDisplayOnly />
        <Space width={8} />
        <SkeletonLoader
          height={
            responsive.desktop
              ? SkeletonLoader.HEIGHT.Typography.Body
              : SkeletonLoader.HEIGHT.Typography.Subheading
          }
          width={80}
        />
      </Row>
    </React.Fragment>
  );
};

const ProjectDetail = ({icon, text}: {icon: IconSource; text: string}) => {
  return (
    <React.Fragment>
      <Space height={8} />
      <Row>
        <IconButton source={icon} isSecondary isDisplayOnly />
        <Space width={8} />
        <Text.Body style={{color: colors.gray.secondary}}>{text}</Text.Body>
      </Row>
    </React.Fragment>
  );
};

const Header = ({project}: {project: ProjectModel}) => {
  const {isEnabledSalesLeadsListPage} = project.organization.features;
  return (
    <WidgetOverviewSection>
      <Row>
        <ProjectSalesStageBadgeDropdown
          project={project}
          organization={project.organization}
          isEditable
        />
      </Row>
      <Space height={isEnabledSalesLeadsListPage ? 8 : 16} />
      <Row>
        {!isEnabledSalesLeadsListPage && (
          <React.Fragment>
            <ProjectStatusBadge project={project} isEditable />
            <Space width={8} />
          </React.Fragment>
        )}
        <Text.Subheading>{Project.getName(project)}</Text.Subheading>
      </Row>
      <Space height={16} />
      <Text.Label>{project.client.name}</Text.Label>
      <Space height={8} />
      <Row>
        <Text.Micro>{project.organization.name}</Text.Micro>
        <Space width={24} />
        <ProjectTypeLabel
          projectType={project.projectType}
          style={{marginLeft: 4}}
          tooltip={'Project type'}
        />
      </Row>
      <Space height={8} />
    </WidgetOverviewSection>
  );
};

const ProjectData = ({project}: {project: ProjectModel}) => {
  return (
    <React.Fragment>
      <ProjectDetail icon={Icon.Calendar} text={Project.getDate(project)} />
      {project.size ? (
        <React.Fragment>
          <ProjectDetail icon={Icon.HouseBuilding} text={project.size} />
        </React.Fragment>
      ) : null}
      <ProjectDetail icon={Icon.WeightHanging} text={`${project.weight} lbs`} />
      <ProjectDetail icon={Icon.RulerTriangle} text={`${project.volume} ft³`} />
      <ProjectDetail icon={Icon.MapPin} text={Project.getLocation(project)} />
    </React.Fragment>
  );
};

const ProjectDetailsOverviewBlockContent = ({
  project,
  refetch,
}: {
  project: ProjectModel;
  refetch: () => void;
}) => {
  return (
    <React.Fragment>
      {project.isCancelled && (
        <React.Fragment>
          <Callout
            text={`Cancelled at: ${Datetime.convertToDisplayDatetime(project.cancelledAt)}`}
          />
          <Space height={16} />
        </React.Fragment>
      )}
      <Header project={project} />
      {project.description && (
        <React.Fragment>
          <Space height={16} />
          <Text.Body>{project.description}</Text.Body>
        </React.Fragment>
      )}
      <Space height={16} />
      <ProjectTagsList project={project} refetch={refetch} />
      <Space height={16} />
      <ProjectData project={project} />
    </React.Fragment>
  );
};

const ProjectDetailsOverviewBlock = ({
  projectUuid,
  index,
}: {
  projectUuid: string;
  index: number;
}) => {
  return (
    <ProjectDetailsBlockWrapper
      key={projectUuid}
      query={ProjectDetailsOverviewBlock.query}
      queryVariables={{projectUuid}}
      index={index}
      SkeletonComponent={SkeletonComponent}
    >
      {({data, refetch}: {data: {project: ProjectModel}; refetch: () => void}) => {
        return <ProjectDetailsOverviewBlockContent project={data.project} refetch={refetch} />;
      }}
    </ProjectDetailsBlockWrapper>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectDetailsOverviewBlock.query = gql`
  ${ProjectTypeLabel.fragment}
  ${Project.getDate.fragment}
  ${Project.getLocation.fragment}
  ${Project.getName.fragment}
  ${ProjectStatusBadge.fragment}
  ${ProjectTagsList.fragment}
  ${ProjectSalesStageBadgeDropdown.projectFragment}
  ${ProjectSalesStageBadgeDropdown.organizationFragment}

  query ProjectDetailsOverviewBlock($projectUuid: String!) {
    project(uuid: $projectUuid) {
      id
      description
      weight
      volume
      size
      isCancelled
      cancelledAt
      isStorage
      organization {
        id
        name
        features {
          isEnabledSalesLeadsListPage: isEnabled(feature: "SALES_LEADS_LIST_PAGE")
        }
        ...ProjectSalesStageBadgeDropdown_Organization
      }
      client {
        id
        name
      }
      ...ProjectTypeLabel
      ...Project_getDate
      ...Project_getLocation
      ...Project_getName
      ...ProjectStatusBadge
      ...ProjectTagsList
      ...ProjectSalesStageBadgeDropdown_Project
    }
  }
`;

export default ProjectDetailsOverviewBlock;
